import { useEffect, useState } from 'react'
import { useMatch } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ArrowUp } from 'lucide-react'
import { WhatsAppBrandIcon } from '@/components/icons/SocialBrandIcons'
import { scrollToTop } from '@/lib/scrollToTop'
import { cn } from '@/lib/utils'

const FOOTER_GAP_PX = 10
const SCROLL_TOP_THRESHOLD = 0.5

interface StoreFloatingActionsProps {
  phone?: string
}

function useFloatingActionsPosition() {
  const [liftPx, setLiftPx] = useState(0)
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    const update = () => {
      const doc = document.documentElement
      const scrollTop = doc.scrollTop
      const scrollable = Math.max(doc.scrollHeight - window.innerHeight, 1)
      setShowScrollTop(scrollTop / scrollable >= SCROLL_TOP_THRESHOLD)

      const distanceFromBottom =
        doc.scrollHeight - scrollTop - window.innerHeight
      const atPageBottom = distanceFromBottom <= 2

      if (!atPageBottom) {
        setLiftPx(0)
        return
      }

      const footerCredit = document.getElementById('store-footer-credit')
      setLiftPx(
        footerCredit
          ? footerCredit.offsetHeight + FOOTER_GAP_PX
          : FOOTER_GAP_PX
      )
    }

    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  return { liftPx, showScrollTop }
}

export function StoreFloatingActions({ phone }: StoreFloatingActionsProps) {
  const isProductDetail = Boolean(useMatch('/shop/:id'))
  const reduceMotion = useReducedMotion() ?? false
  const { liftPx, showScrollTop } = useFloatingActionsPosition()

  const anchorClass = cn(
    'fixed z-50 transition-transform duration-300',
    isProductDetail
      ? 'product-mobile-bar-offset-bottom lg:bottom-6'
      : 'bottom-6'
  )

  const liftStyle =
    liftPx > 0 ? { transform: `translateY(-${liftPx}px)` } : undefined

  return (
    <>
      <div className={cn(anchorClass, 'left-6')} style={liftStyle}>
        <AnimatePresence>
          {showScrollTop && (
            <motion.button
              type="button"
              initial={
                reduceMotion ? false : { opacity: 0, scale: 0.85, y: 16 }
              }
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={
                reduceMotion ? undefined : { opacity: 0, scale: 0.85, y: 16 }
              }
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              onClick={() => scrollToTop({ smooth: !reduceMotion })}
              className={cn(
                'scroll-top-btn relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-full',
                'text-luxury-950 shadow-lg shadow-gold-500/30',
                'transition hover:shadow-gold-400/40',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60'
              )}
              aria-label="Scroll to top"
            >
              {!reduceMotion && (
                <>
                  <motion.span
                    className="pointer-events-none absolute inset-x-3 bottom-1 h-8 rounded-full bg-luxury-950/10 blur-[1px]"
                    animate={{ y: [10, -14], opacity: [0.5, 0] }}
                    transition={{
                      duration: 1.4,
                      repeat: Infinity,
                      ease: 'easeOut',
                    }}
                    aria-hidden
                  />
                  <motion.span
                    className="relative flex flex-col items-center"
                    animate={{ y: [3, -3, 3] }}
                    transition={{
                      duration: 1.4,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                    aria-hidden
                  >
                    <ArrowUp className="h-5 w-5" strokeWidth={2.5} />
                    <ArrowUp
                      className="-mt-3 h-5 w-5 opacity-45"
                      strokeWidth={2.5}
                    />
                  </motion.span>
                </>
              )}
              {reduceMotion && (
                <ArrowUp className="h-6 w-6" strokeWidth={2.5} />
              )}
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {phone ? (
        <div className={cn(anchorClass, 'right-6')} style={liftStyle}>
          <a
            href={`https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent('Hello Bazm-e-Musk, I need help with an order.')}`}
            target="_blank"
            rel="noreferrer"
            className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-black/40 transition hover:scale-105"
            aria-label="WhatsApp support"
          >
            <WhatsAppBrandIcon className="h-7 w-7" />
          </a>
        </div>
      ) : null}
    </>
  )
}

/** @deprecated Use StoreFloatingActions */
export function WhatsAppButton(props: StoreFloatingActionsProps) {
  return <StoreFloatingActions {...props} />
}
